import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Search as SearchIcon } from "lucide-react";
import { formatTimecode } from "@rec/shared";
import { api } from "@/lib/api";
import { Card, EmptyState, ErrorState, Skeleton } from "@/components/ui";
import { Trans, useTranslation } from "react-i18next";

/**
 * Render a `ts_headline` snippet with its matches emphasised.
 *
 * Parsed into React elements rather than injected as HTML. `ts_headline` does
 * NOT escape the source text — it wraps matched terms in <mark> and passes
 * everything else through verbatim — so `dangerouslySetInnerHTML` here would
 * execute any markup that made it into a transcript. Speech-to-text rarely
 * produces HTML, but "rarely" is not a security boundary.
 */
function Highlighted({ snippet }: { snippet: string }) {
  const parts = snippet.split(/(<mark>.*?<\/mark>)/g);
  return (
    <>
      {parts.map((part, index) =>
        part.startsWith("<mark>") ? (
          <mark
            key={index}
            className="rounded bg-accent-subtle px-0.5 text-text"
          >
            {part.slice(6, -7)}
          </mark>
        ) : (
          <span key={index}>{part}</span>
        ),
      )}
    </>
  );
}

/**
 * Search across every transcript in the workspace.
 *
 * Each result links to `?t=<ms>` on the recording, so a hit is a place in a
 * meeting rather than a document reference — that is the whole reason to search
 * a recording instead of a wiki.
 */
export function SearchPage() {
  const { t } = useTranslation(["ai", "common"]);
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");

  // Debounce so a fast typist does not issue a query per keystroke against
  // full-text search.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(input.trim()), 250);
    return () => clearTimeout(timer);
  }, [input]);

  const results = useQuery({
    queryKey: ["search", query],
    queryFn: () => api.search(query),
    enabled: query.length >= 2,
  });

  return (
    <div className="mx-auto max-w-[820px] space-y-5">
      <div className="relative">
        <SearchIcon
          size={17}
          className="absolute top-1/2 left-3.5 -translate-y-1/2 text-text-faint"
        />
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t("search.placeholder")}
          autoFocus
          className="h-12 w-full rounded-xl border border-border bg-surface pr-4 pl-11 text-[15px] text-text placeholder:text-text-faint focus:border-accent focus:outline-none"
        />
      </div>

      {query.length < 2 ? (
        <p className="px-1 text-[13px] text-text-muted">
          <Trans
            t={t}
            i18nKey="search.hint"
            components={{ mono: <span className="font-mono" /> }}
          />
        </p>
      ) : results.isError ? (
        <ErrorState
          title={t("search.failed")}
          error={results.error}
          onRetry={() => void results.refetch()}
        />
      ) : results.isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-20 w-full rounded-xl" />
          ))}
        </div>
      ) : !results.data || results.data.length === 0 ? (
        <EmptyState
          title={t("search.emptyTitle")}
          description={t("search.emptyBody")}
        />
      ) : (
        <div className="space-y-2">
          {results.data.map((hit) => (
            <Link
              key={hit.utteranceId}
              to="/r/$recordingId"
              params={{ recordingId: hit.recordingId }}
              search={{ t: hit.startMs }}
              className="block"
            >
              <Card className="p-4 transition-colors hover:border-border-strong hover:bg-surface-hover">
                <div className="flex items-baseline justify-between gap-3">
                  <span dir="auto" className="truncate text-[13px] font-medium text-text">
                    {hit.recordingTitle}
                  </span>
                  <span className="shrink-0 font-mono text-[11.5px] tabular-nums text-text-faint">
                    {formatTimecode(hit.startMs)}
                  </span>
                </div>
                <p dir="auto" className="mt-1.5 text-[14px] leading-relaxed text-text-muted">
                  <Highlighted snippet={hit.snippet} />
                </p>
                <span className="mt-1.5 block text-[12px] text-text-faint">
                  {hit.speakerLabel}
                </span>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
